import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  IImageBreakpoint,
  MediaObjectFit,
} from '@epicuro-next/ataraxia/models';
import { VideoPlayerComponent } from '@epicuro-next/ataraxia/video-player';
import { VimeoPlayerComponent } from '@epicuro-next/ataraxia/vimeo-player';
import { YoutubePlayerComponent } from '@epicuro-next/ataraxia/youtube-player';
import { dynamicElementsMediaPreview } from '@epicuro-next/constants/dynamic-elements';
import { isVimeo } from '@epicuro-next/features/vimeo';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { isNonNullish } from '@epicuro-next/utilities';
import { Media, MediaType } from '@epicuro-next/models/engagement';
import { BehaviorSubject } from 'rxjs';
import { delay, shareReplay } from 'rxjs/operators';

// FIXME boundaries
// tslint:disable-next-line:nx-enforce-module-boundaries

export interface IMediaVideoAttributes {
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
}

const defaultVideoAttributes: IMediaVideoAttributes = {
  autoplay: false,
  muted: true,
  controls: false,
};

// TODO this should support automatic thumbnail flag for videos
@DynamicComponentRegister(dynamicElementsMediaPreview, true)
@Component({
  selector: 'atx-media-preview',
  templateUrl: './media-preview.component.html',
  styleUrls: ['./media-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MediaPreviewComponent implements OnChanges, AfterViewInit {
  public videoAttributes = defaultVideoAttributes;
  private firstVideoAttributesHandled = false;

  @Input()
  public set enablePictureInPicture(value: boolean) {
    this.pictureInPictureEnabled$$.next(value);
  }

  @Input('videoAttributes')
  public set videoAttributesSetter(attributes: IMediaVideoAttributes) {
    this.videoAttributes = Object.assign({}, this.videoAttributes, attributes);
    if (this.activateMedia) {
      this.videoAttributes.autoplay = true;
    }

    this.handleAutoPlayOnVideoStart();
  }

  @Input()
  public set activateMedia(value: boolean) {
    this.shouldVideoPlay = value ?? undefined;
  }

  constructor(private element: ElementRef, private cdr: ChangeDetectorRef) {}

  public get isVimeo() {
    return isVimeo(this.media.url);
  }

  public get posterUrl() {
    const thumbnails = this.media.thumbnails;
    return thumbnails ? thumbnails[0] : undefined;
  }

  public get mediaUrl() {
    const fallbackUrl = this.media.videoUrl
      ? this.media.videoUrl
      : this.media.url;

    if (!this.showPreviewMedia || this.media?.subclipUrl?.length === 0) {
      return fallbackUrl;
    }

    return this.media.subclipUrl ? this.media.subclipUrl : fallbackUrl;
  }

  private get currentPlayer() {
    switch (this.media.mediaType) {
      case MediaType.Youtube:
        return this.youtubePlayer;
      case MediaType.Vimeo:
        return this.vimeoPlayer;
      case MediaType.Video:
        if (isVimeo(this.media.url)) {
          return this.vimeoPlayer;
        }

        return this.videoPlayer;

      default:
        return null;
    }
  }
  public shouldVideoPlay?: boolean;
  public mediaType = MediaType;
  private pictureInPictureEnabled$$ = new BehaviorSubject(false);
  public pictureInPictureEnabled$ = this.pictureInPictureEnabled$$.pipe(
    delay(0), // this is not good, but it makes the job
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  @Input()
  public media: Media;

  @Input()
  public epicShowPlayerTemplateRef: TemplateRef<unknown>;

  @Input()
  public fallbackImageUrl: string;

  /** Object fit strategy to be passed into children */
  @Input()
  public objectFit: MediaObjectFit = MediaObjectFit.CONTAIN;

  /** Maxwidth to be passed into children */
  @Input()
  public maxWidth?: number;

  /** Max height to be passed into children */
  @Input()
  public maxHeight?: number;

  /** Image breakpoints to be passed into atx-image */
  @Input()
  public imageBreakpoints?: IImageBreakpoint[];

  @Input()
  public startVideoAdvancedBy?: number;

  @Input()
  public activateOnMouseEnter = false;

  @Input()
  public showPreviewMedia = false;

  @Output()
  public mediaReady = new EventEmitter();

  @Output()
  public mediaLoadError = new EventEmitter();

  @ViewChild(YoutubePlayerComponent)
  public youtubePlayer: YoutubePlayerComponent;

  @ViewChild(VideoPlayerComponent)
  public videoPlayer: VideoPlayerComponent;

  @ViewChild(VimeoPlayerComponent)
  public vimeoPlayer: VimeoPlayerComponent;

  @HostBinding('class.contain')
  public getContainClass() {
    return this.objectFit === 'contain' ? 'contain' : undefined;
  }

  @HostBinding('class.cover')
  public getCoverClass() {
    return this.objectFit === 'cover' ? 'cover' : undefined;
  }

  @HostListener('mouseenter')
  public onMouseEnter() {
    if (this.activateOnMouseEnter) {
      this.handleActivateOnHover(true);
    }
  }

  @HostListener('mouseleave')
  public onMouseLeave() {
    if (this.activateOnMouseEnter) {
      this.handleActivateOnHover(false);
    }
  }

  public ngOnChanges({ media }: SimpleChanges) {
    if (media && this.startVideoAdvancedBy) {
      this.handleVideoStartTime();
    }
  }

  public ngAfterViewInit() {
    this.handleVideoStartTime();
  }

  public handleActivateOnHover(show: boolean) {
    this.shouldVideoPlay = show;
    this.cdr.markForCheck();
  }

  public videoVisibilityChange(visible: boolean) {
    if (!this.currentPlayer) {
      return;
    }

    if (!visible) {
      this.currentPlayer.requestPictureInPicture();
    } else {
      this.currentPlayer.exitPictureInPicture();
    }
  }

  public handleVideoStartTime() {
    if (
      !this.startVideoAdvancedBy ||
      (this.showPreviewMedia &&
        this.mediaType.Video &&
        isNonNullish(this.media?.subclipUrl))
    )
      return;

    if (this.videoElm) {
      this.videoElm.currentTime = this.startVideoAdvancedBy ?? 0;
    }
  }

  private get videoElm() {
    return this.element.nativeElement.querySelector(
      'video',
    ) as HTMLVideoElement;
  }

  private handleAutoPlayOnVideoStart() {
    if (this.firstVideoAttributesHandled) return;
    if (this.videoAttributes.autoplay && !this.shouldVideoPlay) {
      this.shouldVideoPlay = true;
    }
    this.firstVideoAttributesHandled = true;
  }
}
